import { useEffect, useRef, useState } from 'react';
import { Play, Search, Sparkles, ArrowLeft, Tag, Users, GraduationCap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { collection, getDocs, getCountFromServer, query, where, limit } from 'firebase/firestore';
import { db } from '../../firebase';

/* ── useInView ── */
function useInView(opts = {}) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVis(true); obs.unobserve(el); }
    }, { threshold: 0.1, ...opts });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}

/* ── Animated counter ── */
function useCounter(target, start, dur = 2000) {
  const [c, setC] = useState(0);
  useEffect(() => {
    if (!start || !target) return;
    let s, raf;
    const go = (t) => {
      if (!s) s = t;
      const p = Math.min((t - s) / dur, 1);
      setC(Math.floor((1 - Math.pow(1 - p, 4)) * target));
      if (p < 1) raf = requestAnimationFrame(go); else setC(target);
    };
    raf = requestAnimationFrame(go);
    return () => cancelAnimationFrame(raf);
  }, [target, start]);
  return c;
}

const TAGS = ['تصميم شعارات', 'هوية بصرية', 'UI/UX', 'إبداع حر', 'AI Design', 'مونتاج'];
const CATEGORIES = ['تصميم جرافيك', 'UI/UX', 'Brand Identity', 'موشن جرافيك'];

export default function HomeMosaic() {
  const [ref, isVisible] = useInView();

  const [data, setData] = useState({ studentCount: 140, courseCount: 5, userPhotos: [] });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const usersSnap = await getCountFromServer(collection(db, 'users'));
        const studentCount = usersSnap.data().count;

        const coursesQ = query(collection(db, 'courses'), where('status', '==', 'published'));
        const coursesDocs = await getDocs(coursesQ);
        const courseCount = coursesDocs.size;

        const photos = [];
        const usersDocs = await getDocs(query(collection(db, 'users'), limit(20)));
        usersDocs.forEach(doc => {
          const u = doc.data();
          if (u.photoURL && photos.length < 5) {
            photos.push(u.photoURL);
          }
        });

        // Fill remaining empty spots with random real-looking internet avatars
        while (photos.length < 5) {
          photos.push(`https://i.pravatar.cc/150?u=${Math.random().toString(36).substring(7)}`);
        }

        setData({
          studentCount: studentCount > 140 ? studentCount : 140,
          courseCount: courseCount > 5 ? courseCount : 5,
          userPhotos: photos
        });
      } catch (err) {
        console.error("Error fetching mosaic stats:", err);
      }
    };
    fetchStats();
  }, []);

  const studentCount = useCounter(data.studentCount, isVisible);
  const courseCount = useCounter(data.courseCount, isVisible);

  const cardBase = "bg-white/70 dark:bg-zinc-900/40 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-3xl hover:-translate-y-2 transition-all duration-500 group relative overflow-hidden shadow-sm hover:shadow-xl dark:shadow-[0_10px_30px_rgba(0,0,0,0.3)] hover:border-primary-500/30 dark:hover:border-primary-500/50";

  const cardStyle = (delay = 0) => ({
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? undefined : 'translateY(30px)',
    transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
  });

  return (
    <section ref={ref} className="py-12 sm:py-16 relative bg-slate-50 dark:bg-black transition-colors duration-500 border-b border-t border-slate-200 dark:border-white/5 z-20 overflow-hidden">
      {/* Cube-pattern background */}
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 dark:opacity-[0.02] mix-blend-overlay pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-14" style={cardStyle(0)}>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-3 sm:mb-4 tracking-tight">
            تجربة تعليمية <span className="text-transparent bg-clip-text bg-gradient-to-l from-primary-600 via-primary-500 to-primary-700 dark:from-primary-400 dark:via-primary-500 dark:to-primary-600 font-extrabold">فريدة</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-slate-600 dark:text-zinc-400 font-bold max-w-xl mx-auto leading-relaxed px-2 sm:px-0">
            بيئة متكاملة تجمع الدورات، المشاريع، والمجتمع في مكان واحد.
          </p>
        </div>

        {/* ══════ Bento Grid ══════ */}
        <div className="grid grid-cols-12 auto-rows-[140px] sm:auto-rows-[160px] md:auto-rows-[180px] gap-4 sm:gap-6 max-w-7xl mx-auto">

          {/* ── Card 1: Student Portraits (Top-Left) ── */}
          <div className={`col-span-12 sm:col-span-6 md:col-span-3 bg-orange-500/20 dark:bg-zinc-900/40 row-span-2 ${cardBase} p-5 sm:p-8 flex flex-col items-center justify-center gap-4 sm:gap-5`} style={cardStyle(0.1)}>
            {/* Decorative glow */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/5 dark:bg-primary-500/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-700 ease-out blur-[20px] pointer-events-none" />

            <div className="flex gap-3 sm:gap-4 relative z-10">
              {data.userPhotos.length >= 2 ? (
                <>
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-white dark:border-zinc-700 shadow-lg group-hover:scale-105 transition-transform duration-500 overflow-hidden bg-slate-100 dark:bg-zinc-800">
                    <img src={data.userPhotos[0]} alt="Student" className="w-full h-full object-cover" />
                  </div>
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-white dark:border-zinc-700 shadow-lg group-hover:scale-105 transition-transform duration-500 delay-75 overflow-hidden bg-slate-100 dark:bg-zinc-800">
                    <img src={data.userPhotos[1]} alt="Student" className="w-full h-full object-cover" />
                  </div>
                </>
              ) : (
                <>
                  <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-zinc-800/80 border-4 border-white dark:border-zinc-700 shadow-lg flex items-center justify-center text-3xl group-hover:scale-105 transition-transform duration-500">

                  </div>
                  <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-zinc-800/80 border-4 border-white dark:border-zinc-700 shadow-lg flex items-center justify-center text-3xl group-hover:scale-105 transition-transform duration-500 delay-75">

                  </div>
                </>
              )}
            </div>
            <p className="text-base sm:text-xl font-bold text-slate-500 dark:text-zinc-400 text-center relative z-10">طلابنا المبدعون</p>
          </div>

          {/* ── Card 2: Video Showcase (Center, large) ── */}
          <div className={`col-span-12 md:col-span-6 row-span-2 ${cardBase} !bg-black overflow-hidden !border-white/[0.08] !p-0`} style={cardStyle(0.2)}>
            <div className="relative w-full h-full min-h-[250px] md:min-h-full flex items-center justify-center">
              {/* Bunny Stream iframe - Replace LIBRARY_ID and VIDEO_ID with your actual IDs */}
              <img
                src="https://s39613.pcdn.co/wp-content/uploads/2019/05/twice-the-brainpower-on-this-assignment-picture-id947895256.jpg"
                loading="lazy"
                title="Lobab Ad"
                className="absolute top-0 left-0 w-full h-full object-cover border-0 pointer-events-auto"
                allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
                allowFullScreen
              ></img>

              {/* Keep an overlay so it acts as an ad card without breaking the landing page layout on accidental clicks if desired. Remove pointer-events-none to allow clicking inside */}
              <div className="absolute inset-0 pointer-events-none rounded-3xl ring-1 ring-inset ring-white/10 z-10" />
            </div>
          </div>

          {/* ── Card 3: Student Count (Top-Right) ── */}
          <div className={`col-span-6 sm:col-span-6 md:col-span-3 row-span-1 ${cardBase} p-4 sm:p-6`} style={cardStyle(0.3)}>
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/5 dark:bg-primary-500/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-700 ease-out blur-[20px] pointer-events-none" />

            <div className="relative z-10 flex flex-col h-full justify-between">
              <div className="w-12 h-12 rounded-2xl bg-slate-50 dark:bg-zinc-800/80 border border-slate-200 dark:border-white/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-500 shadow-sm mb-3">
                <div className="absolute inset-0 bg-primary-500/30 blur-[12px] rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 w-12 h-12" />
                <Users className="w-6 h-6 text-primary-600 dark:text-primary-500 relative z-10" />
              </div>
              <div>
                <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white tabular-nums leading-tight" dir="ltr">
                  {studentCount}+
                </p>
                <p className="text-base sm:text-lg md:text-xl font-bold text-slate-600 dark:text-zinc-400 mt-1">طلاب مدرسة لباب</p>
              </div>
            </div>
          </div>

          {/* ── Card 4: Categories ── */}
          <div className={`col-span-6 sm:col-span-6 md:col-span-3 row-span-1 ${cardBase} p-4 sm:p-6`} style={cardStyle(0.4)}>
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/5 dark:bg-primary-500/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-700 ease-out blur-[20px] pointer-events-none" />

            <div className="relative z-10 flex flex-col h-full">
              <div className="w-12 h-12 rounded-2xl bg-slate-50 dark:bg-zinc-800/80 border border-slate-200 dark:border-white/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-500 shadow-sm mb-4">
                <GraduationCap className="w-6 h-6 text-primary-600 dark:text-primary-500 relative z-10" />
              </div>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((cat, i) => (
                  <span
                    key={i}
                    className="px-3 py-1.5 rounded-lg text-md font-bold bg-slate-50 dark:bg-zinc-800/80 text-slate-700 dark:text-zinc-300 border border-slate-200 dark:border-white/10 hover:border-primary-500/40 transition-colors cursor-default"
                  >
                    {cat}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* ── Card 5: Courses + Search (Bottom-Left) ── */}
          <div className={`col-span-12 sm:col-span-6 md:col-span-4 row-span-1 ${cardBase} p-4 sm:p-6`} style={cardStyle(0.5)}>
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/5 dark:bg-primary-500/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-700 ease-out blur-[20px] pointer-events-none" />

            <div className="relative z-10 flex items-center justify-between h-full">
              <div>
                {/* Avatar pile */}
                <div className="flex -space-x-2 rtl:space-x-reverse mb-3">
                  {data.userPhotos.slice(0, 4).map((photo, i) => (
                    <img key={i} src={photo} alt="Student" className="w-8 h-8 rounded-full border-2 border-slate-50 dark:border-zinc-900 object-cover" />
                  ))}
                </div>
                <p className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-slate-900 dark:text-white tabular-nums" dir="ltr">{courseCount}+</p>
                <p className="text-sm sm:text-base md:text-lg font-medium text-slate-600 dark:text-zinc-400 mt-1">دورات احترافية</p>
              </div>
              <button className="px-5 py-3 bg-slate-50 dark:bg-zinc-800/80 border border-slate-200 dark:border-white/10 rounded-2xl text-base font-bold text-slate-700 dark:text-zinc-300 hover:border-primary-500/40 hover:bg-white dark:hover:bg-zinc-700 transition-all flex items-center gap-2 shadow-sm">
                <Search className="w-4 h-4 text-primary-500" />
                Search
              </button>
            </div>
          </div>

          {/* ── Card 6: Tags Cloud ── */}
          <div className={`col-span-12 sm:col-span-6 md:col-span-5 row-span-1 ${cardBase} p-4 sm:p-6`} style={cardStyle(0.6)}>
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/5 dark:bg-primary-500/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-700 ease-out blur-[20px] pointer-events-none" />

            <div className="relative z-10 flex flex-col h-full justify-center">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-slate-50 dark:bg-zinc-800/80 border border-slate-200 dark:border-white/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-500 shadow-sm">
                  <Tag className="w-5 h-5 text-primary-600 dark:text-primary-500" />
                </div>
                <p className="text-lg font-extrabold text-slate-900 dark:text-white">مهارات المبدعين</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {TAGS.map((tag, i) => (
                  <span
                    key={i}
                    className="px-3 py-1.5 rounded-lg text-md font-bold bg-slate-50 dark:bg-zinc-800/80 text-slate-600 dark:text-zinc-300 border border-slate-200 dark:border-white/10 shadow-sm hover:shadow-md hover:border-primary-500/40 hover:-translate-y-0.5 transition-all cursor-default"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* ── Card 7: CTA Card ── */}
          <div
            className="col-span-12 md:col-span-3 row-span-1 bg-gradient-to-br from-primary-600 to-primary-800 dark:from-primary-700 dark:to-primary-950 backdrop-blur-xl border border-primary-500/30 rounded-3xl p-6 hover:-translate-y-2 transition-all duration-500 group relative overflow-hidden shadow-xl hover:shadow-2xl flex flex-col items-center justify-center text-center"
            style={cardStyle(0.7)}
          >
            {/* Inner glow */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20 group-hover:scale-150 transition-transform duration-700 ease-out blur-[25px] pointer-events-none" />

            <p className="text-white font-extrabold text-xl mb-4 relative z-10">ابدأ رحلتك اليوم</p>
            <Link
              to="/signup"
              className="relative z-10 inline-flex items-center gap-2 px-7 py-3.5 bg-white text-primary-600 rounded-2xl font-bold text-base hover:bg-white/90 transition-all shadow-lg group-hover:shadow-xl group-hover:scale-105"
            >
              <span>انضم الآن</span>
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            </Link>
          </div>

        </div>
      </div>
    </section>
  );
}
